import { MenuItem, Select, Stack, Link, SelectChangeEvent, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { useState, useEffect } from "react";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { routePaths } from "../../../../../Router/router";
import { useParams } from "react-router";
import { orderService } from "../../../../../Core/Api/services";
import { OrderStatus } from "../../../../../Core/Api/Types/order-status";
import DataTable, { CellSize } from "../../../../../Components/Table/table";
import TableCellDateTime from "../../../../../Components/Table/TableCellDateTime/table-cell-date-time";
import TableCellOrderStatus from "../../../../../Components/Table/TableCellOrderStatus/table-cell-order-status";
import TableSkeleton from "../../../../../Components/Table/TableSkeleton";
import { OrderProductType } from "../../../../../Core/Api/Types/order-product-type";
import { Distinct, GetEnumValueLabelMap, GetOrderProductReadableType } from "../../../../../Utils/helpers";
import OrderProductIcon from "../../../../../Components/Misc/OrderProductIcon/order-product-icon";
import OrderItemPanel from "../../../../../Components/Panels/OrderItemPanel/order-item-panel";
import TableCellTypography, {
    TableCellLongTypography,
} from "../../../../../Components/Table/TableCellTypography/table-cell-typography";
import TableCellOwner2Lines from "../../../../../Components/Table/TableCellOwner2Lines/table-cell-owner-2-lines";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../../Components/Table/table-uuids";

export interface AdminTitleOrderRow {
    id: number;
    orderId: number;
    creationDate?: Date | null;
    owner: string | null;
    customerName?: string | null;
    items?: any[] | null;
    comments?: string | null;
    type?: OrderProductType | null;
    status?: OrderStatus | null;
    dueDate?: Date | null;
    ownerUserGroupName?: string | null;
    startTime?: Date | null;
    endTime?: Date | null;
    summarizedInfos: string | null;
}

export interface AdminTitleOrderProps {
    customer: string[];
    status: string[];
    types: OrderProductType[];
}

function AdminTitleOrders() {
    const { t } = useTranslation();
    const [filtersData, setFiltersData] = useState<AdminTitleOrderProps>({ customer: [], status: [], types: [] });
    const [rows, setDataGrid] = useState<GridRowsProp>([]);

    const [customerFilter, setCustomerFilter] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<OrderStatus | "">("");
    const [typeFilter, setTypeFilter] = useState<OrderProductType | "">("");
    const [searchValue, setSearchValue] = useState("");

    const [isLoading, setIsLoading] = useState(true);

    const [showPanel, setShowPanel] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(-1);

    const params = useParams();
    const titleId: number = parseInt(params.id!) || 0;
    type Row = (typeof rows)[number];

    const columns: GridColDef[] = [
        {
            field: "orderId",
            minWidth: CellSize.xsmall,
            flex: CellSize.xsmall,
            headerName: t("menu:title.orderId"),
            renderCell: (data: Row) => (
                <Link
                    color="primary.text"
                    underline="hover"
                    component={RouterLink}
                    to={`${routePaths.ordersOverview.path}/${data.value}`}
                >
                    {data.value}
                </Link>
            ),
        },
        {
            field: "id",
            headerName: t("table:headerName.itemId"),
            minWidth: CellSize.xsmall,
            flex: CellSize.xsmall,
            renderCell: (data: Row) => data.value,
        },
        {
            field: "creationDate",
            headerName: t("common:created"),
            type: "date",
            valueGetter: (data: any, row: any) => (row?.creationDate === null ? null : new Date(row?.creationDate)),
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: Row) => <TableCellDateTime data={{ value: data?.row?.creationDate }} />,
        },
        {
            field: "ownerUserGroupName",
            headerName: t("common:owner"),
            minWidth: CellSize.large,
            flex: CellSize.large,
            renderCell: (data: any) => (
                <TableCellOwner2Lines userName={data?.row?.owner} groupName={data?.row?.ownerUserGroupName} />
            ),
        },
        {
            field: "customerName",
            headerName: t("common:customer"),
            minWidth: CellSize.medium,
            flex: CellSize.medium,
            renderCell: (data: Row) => (data.value ? <TableCellTypography data={data.value} /> : "N/A"),
        },
        {
            field: "type",
            headerName: t("common:type"),
            minWidth: CellSize.xsmall,
            flex: CellSize.xsmall,
            type: "singleSelect",
            valueOptions: GetEnumValueLabelMap(OrderProductType), //[{ value: "Video", label: "Video Label" }],
            renderCell: (data: Row) => <OrderProductIcon type={data.value} color="info" />,
        },
        {
            field: "summarizedInfos",
            headerName: t("table:headerName.orderSpecifications"),
            minWidth: CellSize.xlarge,
            flex: CellSize.xlarge,
            renderCell: (data: Row) => <TableCellLongTypography data={data.value} sx={{ p: 0.5 }} variant="body1" />,
        },
        {
            field: "status",
            headerName: t("common:status"),
            minWidth: CellSize.medium,
            flex: CellSize.medium,
            renderCell: (data: Row) => <TableCellOrderStatus data={data} />,
        },
        {
            field: "startTime",
            headerName: t("common:startDateLowerCase"),
            type: "date",
            valueGetter: (data: any, row: any) => (row?.startTime === null ? null : new Date(row?.startTime)),
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: any) =>
                data.value === null ? (
                    <Typography variant="body2" color="text.tier">
                        {t("common:notStarted")}
                    </Typography>
                ) : (
                    <TableCellDateTime data={{ value: data?.row?.startTime }} />
                ),
        },
        {
            field: "endTime",
            headerName: t("common:endDateLowerCase"),
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: any) =>
                data.value === null ? (
                    <Typography variant="body2" color="text.tier">
                        {t("table:renderCell.notEnded")}
                    </Typography>
                ) : (
                    <TableCellDateTime data={{ value: data?.row?.endTime }} />
                ),
        },
    ];

    useEffect(() => {
        if (titleId !== 0) {
            orderService.GetOrdersItemsListByTitleId(titleId, 0, 2000).then(function (response) {
                if (response !== null) {
                    let customers: string[] = [];
                    response.map((order) => {
                        if (
                            order.customerName !== null &&
                            order.customerName !== undefined &&
                            order.customerName !== "" &&
                            !customers.includes(order.customerName)
                        )
                            customers.push(order.customerName);
                    });

                    setFiltersData({
                        customer: customers,
                        status: response.map((order) => order.status!).filter(Distinct),
                        types: response.map((order) => order.type!).filter(Distinct),
                    });
                    setDataGrid(response);
                    setIsLoading(false);
                }
            });
        }
    }, [titleId]);

    const handleChangeCustomerSelect = (event: SelectChangeEvent) => {
        setCustomerFilter(event.target.value);
    };

    const handleChangeStatusSelect = (event: SelectChangeEvent) => {
        setStatusFilter(event.target.value === "" ? "" : (event.target.value as OrderStatus));
    };

    const handleChangeTypeSelect = (event: SelectChangeEvent) => {
        setTypeFilter(event.target.value === "" ? "" : (event.target.value as OrderProductType));
    };

    const updateSearchValue = (text: string) => {
        setSearchValue(text);
    };

    // Trig open panel
    const handleShowPanelOrder = (e: any) => {
        setSelectedRowId(e.id);
        setShowPanel(true);
    };

    const additionalFilters = (
        <>
            <Select
                displayEmpty
                startAdornment={<HandshakeOutlinedIcon />}
                IconComponent={ExpandMoreRoundedIcon}
                value={customerFilter}
                inputProps={{ "aria-label": "Without label" }}
                onChange={handleChangeCustomerSelect}
                MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
            >
                <MenuItem value="">{t("common:customer")}</MenuItem>
                {filtersData.customer.length > 0 &&
                    filtersData.customer.map((customer) => (
                        <MenuItem key={customer} value={customer}>
                            {customer}
                        </MenuItem>
                    ))}
            </Select>
            <Select
                displayEmpty
                startAdornment={<EqualizerIcon />}
                IconComponent={ExpandMoreRoundedIcon}
                value={statusFilter}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                onChange={handleChangeStatusSelect}
            >
                <MenuItem value="">{t("common:status")}</MenuItem>
                {filtersData.status.length > 0 &&
                    filtersData.status.map((status) => (
                        <MenuItem key={status} value={status}>
                            {status}
                        </MenuItem>
                    ))}
            </Select>
            <Select
                displayEmpty
                startAdornment={<MergeTypeIcon />}
                IconComponent={ExpandMoreRoundedIcon}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={typeFilter}
                onChange={handleChangeTypeSelect}
            >
                <MenuItem value="">{t("common:type")}</MenuItem>
                {filtersData.types.length > 0 &&
                    filtersData.types.map((type) => (
                        <MenuItem key={type} value={type}>
                            {GetOrderProductReadableType(type)}
                        </MenuItem>
                    ))}
            </Select></>
    )

    return (
        <Stack minHeight={"calc(100vh - 129px)"}>
            <DataTable
                loading={isLoading}
                tableKey={TableUuids.TITLE_SINGLE_ORDERS}
                additionalFilters={additionalFilters}
                rows={rows.filter(
                    (x) =>
                        (customerFilter === "" || x.customerName === customerFilter) &&
                        (statusFilter === "" || x.status === statusFilter) &&
                        (typeFilter === "" || x.type === typeFilter) &&
                        (searchValue === "" ||
                            x.id.toString() === searchValue ||
                            x.comments?.includesAllWords(searchValue) ||
                            x.summarizedInfos?.includesAllWords(searchValue))
                )}
                columns={columns}
                getRowHeight={() => "auto"}
                disableRowSelectionOnClick
                pagination={true}
                onRowClick={handleShowPanelOrder}
                initialState={{
                    sorting: { sortModel: [{ field: "creationDate", sort: "desc" }] },
                    pinnedColumns: { left: ["orderId", "id", "ownerUserGroupName"], right: ["actions"] },
                }}
            />
            <OrderItemPanel open={showPanel} onClose={() => setShowPanel(false)} itemId={selectedRowId} />
        </Stack>
    );
}

export default AdminTitleOrders;
